// src/components/BlocoKitBlindagem.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControlLabel,
  Checkbox,
  Chip,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import api from "../services/api";

/* ===== Helpers (SEM timeZone, usando apenas o relógio local) ===== */
function dataHojeSP() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`; // YYYY-MM-DD
}

function somarDiasYMD(ymd, dias) {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = Date.UTC(y, m - 1, d) + dias * 86400000;
  return new Date(t).toISOString().slice(0, 10);
}

function formatarDataBR(v) {
  if (!v) return "—";
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

function chipStatus(status) {
  switch (status) {
    case "pendente":
      return {
        label: "pendente",
        sx: { backgroundColor: "#e53935", color: "#fff", fontWeight: "bold" },
      };

    case "pago":
      return {
        label: "pago",
        sx: { backgroundColor: "#43a047", color: "#fff", fontWeight: "bold" },
      };

    case "brinde":
      return {
        label: "brinde",
        sx: { backgroundColor: "#039BE5", color: "#fff", fontWeight: "bold" },
      };

    case "credito":
    case "confirmado_credito":
      return {
        label: "crédito",
        sx: { backgroundColor: "#8e24aa", color: "#fff", fontWeight: "bold" },
      };

    case "confirmado":
      return {
        label: "confirmado",
        sx: { backgroundColor: "#FFEB3B", color: "#333", fontWeight: "bold" },
      };

    case "desativado":
      return {
        label: "desativado",
        sx: { backgroundColor: "#222", color: "#fff", fontWeight: "bold" },
      };

    default:
      return { label: status || "-", sx: {} };
  }
}

export default function BlocoKitBlindagem({
  form,
  setForm,
  podeEditarKit,
  isAdmin,
  editando,
  onSaved = () => {},
  saldoCreditos = 0,
  onUsarCredito,
  usandoCredito = false,
  kitRefresh = 0,
}) {
  const [obs, setObs] = useState("");
  const [hist, setHist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [touchedDate, setTouchedDate] = useState(false);

  const usuarioId = localStorage.getItem("id_usuario") || 0;

  // ====== Derivados de data/estado para a UI ======
  const hoje = dataHojeSP();
  const vencidoKit =
    !form.kit_blindagem_vencimento ||
    String(form.kit_blindagem_vencimento) < hoje;

  // checkbox visual reflete vencido
  const checkedUI = !!form.kit_blindagem && !vencidoKit;

  // saldo / cor do badge
  const saldo = Number(saldoCreditos ?? 0);
  const temCreditos = saldo > 0;
  const maxDiasKit = isAdmin ? 99999 : (temCreditos ? 365 : 30);
  const dataMaxKit = somarDiasYMD(hoje, maxDiasKit);

  useEffect(() => {
    if (!editando || !form.chave) return;
    carregarHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitRefresh]);

  // não-admin: marcou -> hoje + limite permitido (30 ou 365); desmarcou -> limpa
  useEffect(() => {
    if (!isAdmin && form.kit_blindagem) {
      const padrao = somarDiasYMD(dataHojeSP(), maxDiasKit);
      setForm((prev) => ({ ...prev, kit_blindagem_vencimento: padrao }));
      setTouchedDate(true);
    } else if (!isAdmin && !form.kit_blindagem) {
      setForm((prev) => ({ ...prev, kit_blindagem_vencimento: "" }));
      setTouchedDate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.kit_blindagem, isAdmin, maxDiasKit]);

  useEffect(() => {
    if (form.kit_blindagem && !form.kit_blindagem_vencimento) {
      setForm((prev) => ({
        ...prev,
        kit_blindagem_vencimento: dataHojeSP(),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editando && form.chave) carregarHistorico();
    // eslint-disable-next-line
  }, [form.chave]);

  async function carregarHistorico() {
    setLoading(true);
    try {
      const { data } = await api.get(`/kit-blindagem/${form.chave}/historico`);
      const lista = Array.isArray(data)
        ? data
        : Array.isArray(data?.registros)
        ? data.registros
        : [];
      setHist(lista);
    } catch (e) {
      if (e.response?.status !== 404)
        console.error("Erro ao carregar histórico:", e);
      setHist([]);
    } finally {
      setLoading(false);
    }
  }

  async function salvarKitHistorico() {
    if (!editando) {
      alert('Salve a licença antes de lançar o Kit Blindagem.');
      return;
    }
    const habilitarUI = !!(checkedUI || form.kit_blindagem);
    let data_venc = habilitarUI ? form.kit_blindagem_vencimento : null;

    if (habilitarUI && !data_venc) {
      setSnackbar({
        open: true,
        message: "Informe a data de vencimento.",
        severity: "error",
      });
      return;
    }

    setSaving(true);
    try {
      if (!usuarioId || Number(usuarioId) <= 0) {
        setSnackbar({
          open: true,
          message: "Sessão inválida. Faça login novamente.",
          severity: "error",
        });
        return;
      }

      const saldoNow = Number(saldoCreditos ?? 0);
      const temCreditosNow = saldoNow > 0;

      // ✅ Regra: se já existe pendente, NÃO lança outra linha.
      // Se tiver crédito (e não for admin), quita a pendência primeiro via /usar-credito.
      const temPendenteLocal =
        Array.isArray(hist) && hist.some((l) => l.status === "pendente");

      if (habilitarUI && temPendenteLocal) {
        if (!isAdmin && temCreditosNow && onUsarCredito) {
          await onUsarCredito(true);
          await carregarHistorico();
          await onSaved();
          setSnackbar({
            open: true,
            message: "Pendência confirmada usando 1 crédito.",
            severity: "success",
          });
          return;
        }

        setSnackbar({
          open: true,
          message:
            "Já existe uma pendência deste Kit. Confirme a pendência (ou adicione créditos) antes de lançar novamente.",
          severity: "warning",
        });
        return;
      }

      // =========================
      // 1) LANÇAR NO HISTÓRICO
      //    - se TIVER CRÉDITO (representante) → tenta lançar como "crédito"
      //    - se NÃO tiver                     → backend trata como "pendente"
      //    - se desativar                     → status 'desativado'
      // =========================
      const payloadLancar = {
        usuario_id: Number(usuarioId),
        observacao: obs || null,
      };

      if (habilitarUI) {
        payloadLancar.data_vencimento = data_venc;

        if (!isAdmin) {
            // REPRESENTANTE
            // ✅ Sem pendência: se tem crédito -> crédito; se não tem -> pendente
            if (temCreditosNow) {
            await api.post(`/kit-blindagem/${form.chave}/lancar-credito`, payloadLancar);
          } else {
            await api.post(`/kit-blindagem/${form.chave}/lancar-pendente`, payloadLancar);
          }
       } else {
          // ADMIN → lança PENDENTE
          await api.post(
            `/kit-blindagem/${form.chave}/lancar-pendente`,
            payloadLancar
          );
        }
      } else {
        // Desativando → vai como 'desativado' na rota de pendente
        payloadLancar.status = "desativado";
        payloadLancar.data_vencimento = null;

        await api.post(
          `/kit-blindagem/${form.chave}/lancar-pendente`,
          payloadLancar
        );
      }

      // =========================
      // 2) APLICAR NA LICENÇA
      // =========================
      await api.post(`/kit-blindagem/${form.chave}/aplicar`, {
        habilitar: habilitarUI,
        vencimento: data_venc,
        observacao: obs || null,
      });

      setObs("");
      setTouchedDate(false);

      await carregarHistorico();
      await onSaved(); // LicencaNova recarrega licença + saldo

      setSnackbar({
        open: true,
        message:
          !isAdmin && temCreditosNow
            ? "Kit Blindagem lançado usando 1 crédito."
            : "Kit Blindagem lançado com sucesso.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Erro ao lançar Kit Blindagem.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  const temPendente =
    Array.isArray(hist) && hist.some((l) => l.status === "pendente");
  const mostrarAvisoPendente = temPendente && podeEditarKit;

  const handleFlag = (e) => {
    const marcado = e.target.checked;
    setForm((prev) => ({
      ...prev,
      kit_blindagem: marcado,
      ...(marcado
        ? { kit_blindagem_vencimento: somarDiasYMD(dataHojeSP(), maxDiasKit) }
        : { kit_blindagem_vencimento: "" }),
    }));
    setTouchedDate(true);
  };

  const handleDate = (e) => {
    let valor = e.target.value;
  
    if (valor && valor < hoje) {
      valor = hoje;
    }
  
    if (valor && valor > dataMaxKit) {
      valor = dataMaxKit;
    }
  
    setTouchedDate(true);
    setForm((prev) => ({
      ...prev,
      kit_blindagem_vencimento: valor,
    }));
  };

  const handlePlus30 = () => {
    if (temPendente || !podeEditarKit) return;
    const novo = somarDiasYMD(dataHojeSP(), 30); // HOJE + 30 (não acumulativo)
    setForm((prev) => ({ ...prev, kit_blindagem_vencimento: novo }));
    setTouchedDate(true);
  };

  const handlePlus365 = () => {
    if (temPendente || !podeEditarKit || !temCreditos) return;
    const novo = somarDiasYMD(dataHojeSP(), 365);
    setForm((prev) => ({ ...prev, kit_blindagem_vencimento: novo }));
    setTouchedDate(true);
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box mt={4} mb={4} p={2} border="1px solid #ccc" borderRadius={2}>
      {/* Cabeçalho + badge de créditos */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Kit Blindagem</Typography>

        <Box
          sx={{
            px: 2.5,
            py: 0.75,
            borderRadius: 999,
            bgcolor: temCreditos ? "primary.main" : "error.main",
            color: "common.white",
            display: "inline-flex",
            alignItems: "center",
            gap: 1.2,
            boxShadow: 1,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
            }}
          >
            Créditos disponíveis:
          </Typography>

          <Typography
            component="span"
            sx={{
              fontSize: 15,
              fontWeight: 700,
              fontFamily: "monospace",
            }}
          >
            {saldo}
          </Typography>
        </Box>
      </Box>

      {/* Linha de controles */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={checkedUI}
                onChange={handleFlag}
                disabled={!podeEditarKit}
              />
            }
            label="Proteger Contas"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="Data de Vencimento"
            type="date"
            value={form.kit_blindagem_vencimento || ""}
            onChange={handleDate}
            InputLabelProps={{ shrink: true }}
            fullWidth
            disabled={!podeEditarKit || !form.kit_blindagem}
            inputProps={{ min: hoje, max: dataMaxKit }}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={handlePlus30}
              fullWidth
              disabled={temPendente || !podeEditarKit}
            >
              +30 DIAS
            </Button>

            <Button
              variant="outlined"
              onClick={handlePlus365}
              fullWidth
              disabled={temPendente || !podeEditarKit || (!temCreditos && !isAdmin)}
            >
              +365 DIAS
            </Button>
          </Box>
        </Grid>


        <Grid item xs={12} sm={3}>
          <TextField
            label="Nova Observação"
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={1}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={salvarKitHistorico}
            disabled={!editando || !podeEditarKit || saving || usandoCredito}
          >
            {saving ? "Salvando..." : "SALVAR"}
          </Button>
        </Grid>
      </Grid>

      {/* Aviso de pendência + botão para usar crédito */}
      {mostrarAvisoPendente && (
        <Paper
          sx={{
            mt: 3,
            mb: 2,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#FFF8E1",
            border: "1px solid #FFE082",
          }}
          elevation={0}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Existe um lançamento pendente deste Kit Blindagem.
            </Typography>
            <Typography variant="body2">
              Créditos disponíveis:{" "}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                {saldo}
              </Box>
            </Typography>
          </Box>

          {onUsarCredito && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => onUsarCredito(false)}
              disabled={usandoCredito || saldo <= 0}
            >
              {usandoCredito
                ? "Processando..."
                : "CONFIRMAR PENDENTE USANDO 1 CRÉDITO"}
            </Button>
          )}
        </Paper>
      )}

      {/* Histórico */}
      <Box mt={3}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Histórico (somente leitura)
        </Typography>

        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Lançamento</TableCell>
                <TableCell>Confirmação</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Obs.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6}>Carregando...</TableCell>
                </TableRow>
              )}

              {!loading && (!hist || hist.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6}>Nenhum lançamento ainda.</TableCell>
                </TableRow>
              )}

              {!loading &&
                hist &&
                hist.map((linha) => {
                  const st = chipStatus(linha.status);
                  return (
                    <TableRow key={linha.id}>
                      <TableCell>
                        <Chip
                          label={st.label}
                          size="small"
                          sx={{ ...st.sx, height: 22 }}
                        />
                      </TableCell>
                      <TableCell>{formatarDataBR(linha.data_lancamento)}</TableCell>
                      <TableCell>{formatarDataBR(linha.data_confirmacao)}</TableCell>
                      <TableCell>{formatarDataBR(linha.data_vencimento)}</TableCell>
                      <TableCell>{linha.usuario_nome || "—"}</TableCell>
                      <TableCell>{linha.observacao || "—"}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
